import type { Step } from "@ui-kit/components/stepper"

import { computed, type ComputedRef, ref, watch } from "vue"
import type { Composer } from "vue-i18n"
import { useRoute } from "vue-router"
import {
  type ApplyStage,
  useApplicationStore,
  useProviderStore,
} from "@versatile-credit-apply/sdk"

import {
  useStepperDefinition,
  type StepId,
} from "./stepper/use-stepper-definition"

type RetailApplyStep = Omit<Step, "id"> & {
  id: StepId
}

export type StepCondition = ApplyStage | "*"

type StepRouteIdGetter = (steps: Array<StepId>) => StepId

export type StepRouteMeta =
  | StepId
  | Partial<Record<StepCondition, StepId>>
  | StepRouteIdGetter

export interface StepperConfig {
  id?: string
  visible?: boolean
}

const useCurrentStepId = (
  steps: ComputedRef<RetailApplyStep[]>
): ComputedRef<StepId | undefined> => {
  const route = useRoute()
  const store = useApplicationStore()
  return computed(() => {
    const maybeStep = route.meta?.step
    if (!maybeStep) {
      return undefined
    }

    if (typeof maybeStep === "string") {
      return maybeStep
    }

    if (typeof maybeStep === "function") {
      return maybeStep(steps.value.map((step) => step.id))
    }

    if (store.applyStage && maybeStep[store.applyStage]) {
      return maybeStep[store.applyStage]
    }

    return maybeStep["*"]
  })
}

const useStepRepository = (i18n: Composer): ComputedRef<RetailApplyStep[]> => {
  const { t } = i18n

  const stepDefinition = useStepperDefinition()

  const createStep = (id: StepId): RetailApplyStep => ({
    id,
    label: (id) => t(`stepper.steps.${id}.label`),
  })

  return computed(() => stepDefinition.value.steps.map(createStep))
}

const useLastActiveStepIndex = (
  steps: ComputedRef<RetailApplyStep[]>
): ComputedRef<number> => {
  const store = useProviderStore()

  const lastActiveStepIndex = ref<number>(0)
  const currentStepId = useCurrentStepId(steps)

  watch(currentStepId, (newValue, oldValue) => {
    if (newValue && oldValue) {
      lastActiveStepIndex.value = steps.value.findIndex(
        (step) => step.id === newValue
      )
    }
  })

  // Reset stepper on Provider Change
  watch(
    () => store.currentProviderKey,
    () => {
      lastActiveStepIndex.value = 0
    }
  )

  return computed(() => lastActiveStepIndex.value)
}

const useCurrentStepIndex = (
  steps: ComputedRef<RetailApplyStep[]>
): ComputedRef<number> => {
  const lastActiveStepIndex = useLastActiveStepIndex(steps)
  const currentStepId = useCurrentStepId(steps)
  return computed(() => {
    const id = currentStepId.value

    return id
      ? steps.value.findIndex((step) => step.id === id)
      : lastActiveStepIndex.value
  })
}

export const useStepperSteps = (i18n: Composer): ComputedRef<Step[]> => {
  const repository = useStepRepository(i18n)
  const currentStepIndex = useCurrentStepIndex(repository)

  return computed(() => {
    return repository.value.map((step, index): Step => {
      if (currentStepIndex.value === index) {
        return {
          ...step,
          status: "active",
        }
      } else if (currentStepIndex.value > index) {
        return {
          ...step,
          status: "complete",
        }
      } else {
        return {
          ...step,
          status: "inactive",
        }
      }
    })
  })
}
